/**
 * What the operational panes used to explain, now explained once, here.
 *
 * The ruling this module answers: "much of that explanatory prose can probably move into Learn,
 * making the operational panes considerably quieter." Four surfaces carried it — Navigate,
 * Workspace, Inspector and the ask bar — and each carried a different fragment of the same
 * explanation, in a pane whose job is to say what to do at that spot.
 *
 * **The division this module holds to.** A pane's empty state says what to do HERE: one short,
 * concrete sentence with the control beside it. Learn says what any of it MEANS. So "No purposeful
 * model yet. Add one, and state the engineering question it answers" stays in the models rail, next
 * to the `+ Model` row that acts on it, and "the Inspector shows what a selected object carries,
 * which models it appears in, and what its absence would mean" lives here, because that is a fact
 * about the application rather than an instruction for a reader standing in front of an empty pane.
 *
 * **Why it is a typed table and not prose in `learn.html`.** The Learn shell is deliberately
 * content-free: `test/learn-content.test.ts` asserts that no entry anchor appears in it, so a
 * hand-edit cannot turn the page into a brochure that drifts from the registry. This is not
 * registry-derived content and cannot be — nothing in the kernel knows how a pane reads — so it
 * takes the other discipline available: one declaration, with the surface each section VACATED
 * recorded beside it. A node-tier check reads `movedFrom` and fails if the vacated phrase is still
 * in the file it came from, which makes this a MOVE the compiler and the suite can see rather than a
 * copy that leaves both surfaces verbose.
 */

/** One block of a section. Prose, or a labelled list; a section is a short sequence of these. */
export type GuideBlock =
  | { readonly kind: "prose"; readonly text: string }
  | { readonly kind: "bullets"; readonly label: string; readonly items: readonly string[] };

/**
 * A surface this section took prose off, and a phrase that must no longer be in it.
 *
 * The phrase is the teeth. Recording "this came from the ask bar" is a comment; recording a
 * sentence fragment that has to be ABSENT from `index.html` is a cross-file assertion, and it holds
 * in both directions — it fails if the move was really a copy, and it fails again if a later wave
 * puts the verbose sentence back while this page goes on claiming to own it.
 */
export interface VacatedProse {
  /** Package-relative path, as the node tier's cwd sees it. */
  readonly from: string;
  /** What that file no longer says. Long enough to be the sentence and not a common word. */
  readonly vacated: string;
}

export interface GuideSection {
  /** The in-page anchor. Distinct from every registry anchor; the smoke tier checks both sets. */
  readonly anchor: string;
  readonly heading: string;
  /** One sentence under the heading, the shape every gallery section already uses. */
  readonly intro: string;
  readonly blocks: readonly GuideBlock[];
  readonly movedFrom: readonly VacatedProse[];
}

const prose = (text: string): GuideBlock => ({ kind: "prose", text });
const bullets = (label: string, items: readonly string[]): GuideBlock =>
  ({ kind: "bullets", label, items });

/**
 * The guide, in reading order: the panes, then what a property is, then how asking works.
 *
 * That order is the order a reader meets them. The panes are what is on screen; a property is the
 * object the application is for; asking is the act that produces one.
 */
export const WORKBENCH_GUIDE: readonly GuideSection[] = [
  {
    anchor: "guide-the-panes",
    heading: "How the Workbench is laid out",
    intro: "Four panes, and each one answers a different question about the same model system.",
    blocks: [
      bullets("What each pane is for", [
        "Navigate lists the models the system declares and the properties it asserts. Both are "
          + "navigation: choosing a model puts it in the Workspace, and choosing a property shows "
          + "the claim, its verdict and the models that established it.",
        "The Workspace holds one model at a time: its name, its kind, its Description — stored "
          + "prose its author wrote, like a code comment; the Workbench never writes one for you "
          + "— the drawing, and beneath it the Model scope: what the model declares it Includes "
          + "and Omits. The drawing is the principal representation; a structured text reading of "
          + "the same diagram sits behind one disclosure for keyboard and screen-reader use.",
        "The Inspector holds one selected object. It shows what the object is, what it carries, the "
          + "models it appears in, and what its absence would mean.",
        "Ask about the model system — behind the Advanced toggle — takes a question and answers "
          + "it against the current revision. The default surface leaves asking to your agent: "
          + "tell it what you want to know, Refresh, and read the claim and its verdict in "
          + "Navigate.",
      ]),
      prose("The default surface is a viewer: clicking changes what you see, never the model. "
        + "The loop is — instruct your agent; refresh the workbench and inspect the model; "
        + "inspect the properties and their evidence; discuss with your agent; revise. The "
        + "editing surfaces below are all still here, behind the Advanced toggle in the header."),
      prose("In Advanced mode, operations live where their object is. The additive ones — an "
        + "entity, a state, a "
        + "relation, a model, a note — are under + Add in the Workspace, because none of them needs "
        + "anything selected. Renaming, setting a property, connecting and deleting are actions on "
        + "the thing they concern, so they are in the Inspector, on the object itself."),
      prose("A pane with nothing in it says so and says what to do there. It does not explain the "
        + "application; that is this page's job."),
    ],
    movedFrom: [
      // The Inspector's empty sentence ran on into an account of what the pane does. The
      // instruction stayed; the account is the first bullet above.
      {
        from: "src/ui/shell/inspector.ts",
        vacated: "this pane shows what it is, what it carries",
      },
      // The action bar's hint said the same thing a second time, from the other side of the pane.
      {
        from: "src/ui/shell/edit-dialogs.ts",
        vacated: "and the operations that apply to it appear",
      },
      // The `+ Add` menu's hint explained the whole division of operations from inside a menu of
      // five of them.
      {
        from: "index.html",
        vacated: "is an action on the thing it concerns, in the Inspector",
      },
    ],
  },
  {
    anchor: "guide-what-a-property-is",
    heading: "What a property is",
    intro: "A property is a saved claim. The Workbench retains the question that decides the claim "
      + "and recomputes its verdict on demand.",
    blocks: [
      // Deliberately short (author's item 20): the full recomputation account is canonical under
      // "Properties and model changes", and this section had carried it verbatim a second time.
      prose("Because nothing stores the verdict, a property cannot go stale: it reports what the "
        + "models say now. The full account is under “Properties and model changes” above."),
      prose("A verdict always names the models that established it. A bounded search reports itself "
        + "as inconclusive rather than as a no, because a search that stopped at its budget has not "
        + "shown that the thing it looked for is absent."),
      prose("Declaring an expectation makes a property a requirement. If the current outcome "
        + "differs, the requirement is violated."),
    ],
    movedFrom: [
      // The properties rail's empty sentence carried the lifecycle AND the semantics. The
      // lifecycle — ask, then track — is the instruction and stays there.
      {
        from: "src/ui/shell/nav.ts",
        vacated: "it becomes a property evaluated against every later revision",
      },
    ],
  },
  {
    anchor: "guide-how-asking-works",
    heading: "How asking works",
    intro: "You tell your agent what must hold; it formalizes the question and the claim lands in "
      + "Navigate with its verdict. The Advanced ask line, behind the toggle, filters a catalogue "
      + "of askable questions. Free text is not interpreted as English.",
    blocks: [
      prose("The catalogue offers only questions the loaded models license, so a question the "
        + "system cannot answer is one the list does not contain. Typing filters that list."),
      prose("What you type is not interpreted as English. Guessing which formal question an "
        + "English sentence meant would fabricate precision the engine never had, so unmatched text "
        + "is answered by pointing at a route that can take the question instead."),
      bullets("A question in your own words has two homes", [
        "Your coding agent, which drives this same workbench through window.mage.ask. There is one "
          + "workspace, so an agent's edit appears in the ordinary interface immediately.",
        "Advanced query, which states a question formally: a chosen hop limit, two named endpoints, "
          + "an explicit quantifier.",
      ]),
      prose("A question whose kind needs a model type the system does not declare is answered NOT "
        + "ANSWERABLE, with a route to the gallery entry for the missing type. The refusal and this "
        + "page read one registry, so the route cannot offer an entry the kernel would not refuse "
        + "for."),
    ],
    movedFrom: [
      // The ask bar's help paragraph was five sentences of semantics above a one-line text field.
      // The filter claim and the two routes stay, in one sentence each; the rest is above.
      {
        from: "index.html",
        vacated: "A verdict is recomputed on every revision and stored nowhere",
      },
    ],
  },
  {
    // The agent tail (author's instruction, 261006): the site's generated `llms.txt` points an
    // arriving agent HERE, so this section carries the detailed account rather than the index
    // duplicating it. Unlike its siblings it vacated no pane — the material was previously said
    // nowhere on a served page; the co-authoring path was discoverable only by reading test files.
    // `catalog.py` reads this section's anchor + intro at build time (the one guide section whose
    // heading mentions an agent), so renaming or deleting it fails the site build loudly instead of
    // shipping a dead link.
    anchor: "guide-working-with-a-coding-agent",
    heading: "Working with a coding agent",
    intro: "One shared workspace: a human in the page, and a coding agent driving the same model "
      + "system through window.mage.",
    blocks: [
      prose("The Workbench is fully client-side. The engine ships in the page's own bundle, so "
        + "there is no server and no API endpoint behind it. An agent operates the page itself, in "
        + "the browser where it is open."),
      prose("window.mage is the agent surface, and window.mage.describe() is the place to start. "
        + "It publishes the API version, the JSON schemas for models, queries and transactions, "
        + "and the operations. It also states the negative space as plainly as the positive: "
        + "notSupported lists what the Workbench refuses to do, affordanceGaps what it cannot yet "
        + "express, and outsideSemanticInterface what is on screen but deliberately outside the "
        + "semantic surface. An agent that reads describe() first learns the boundary before "
        + "acting, instead of discovering it by failing."),
      prose("Co-authoring runs over Chrome DevTools Protocol attach. The agent attaches to the "
        + "human's existing Chromium — a browser the agent did not launch — and the two share one "
        + "workspace. The agent mutates through window.mage; the human's own session sees the "
        + "hash, the state and the visible page move. It works in the other direction too: a "
        + "control the human operates is observed by the attached agent. The browser tier's "
        + "test/browser/attach.test.mjs drives exactly this flow, deterministically, with no agent "
        + "in the loop."),
      prose("The limit, stated here so it is learned rather than discovered: MAGE opens no port, "
        + "discovers no agent, and holds no socket. The transport — launching Chromium with a "
        + "debugging port, attaching to it, deciding who may attach — belongs to the execution "
        + "environment."),
    ],
    movedFrom: [],
  },
];

/** Every guide anchor. The smoke tier adds these to the registry's when it checks the section set. */
export const GUIDE_ANCHORS: readonly string[] = WORKBENCH_GUIDE.map((s) => s.anchor);
